<script lang="ts">
	/**
	 * Mode selection step for Night Mode
	 * Allows enabling/disabling mode and selecting between RGB/White
	 */

	//Design system components
	import { Container, Toggle, Select, Alert, Text } from '@Shelly/shelly-design-system';
	//Svelte stores
	import { createEventDispatcher, getContext } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	//Helpers
	import { supportsColorModeChange } from 'src/helpers/night_mode_helpers';

	export let disabled = false;

	const nightModeStore: SvelteStore<any> = getContext('nightMode');
	const dispatch = createEventDispatcher();

	let mode = $nightModeStore.mode;
	let isModeEnabled = mode !== null && mode !== undefined;

	// Check if device supports mode change
	$: deviceSupportsColorModeChange = supportsColorModeChange($nightModeStore?.deviceType || '');

	$: modeOptions = [
		{
			title: $format('button_color', { default: 'Color' }),
			value: 'rgb',
		},
		{
			title: $format('button_white', { default: 'White' }),
			value: 'cct',
		},
	];

	$: selectedMode =
		isModeEnabled && modeOptions
			? modeOptions.find((opt) => opt.value === mode) || modeOptions[0]
			: modeOptions?.[0];

	const handleToggle = () => {
		isModeEnabled = !isModeEnabled;

		if (isModeEnabled) {
			// Enable mode with default value
			mode = selectedMode.value;
			dispatch('update', { mode });
		} else {
			// Disable mode
			mode = null;
			dispatch('update', { mode: null });
		}
	};

	const handleModeChange = (e) => {
		selectedMode = e.detail;
		mode = selectedMode.value;
		dispatch('update', { mode });
	};
</script>

<Container hideSlotTop hideSlotBottom padding="0">
	<Toggle
		id="toggle-mode-enable"
		bind:checked={isModeEnabled}
		size="m"
		class="mb-s"
		on:click={handleToggle}
		{disabled}
	>
		{$format('label_enable', { default: 'Enable' })}
	</Toggle>
	<Text element="h2" class="mb-s"
		>{$format('select_type', {
			values: { type: $format('label_mode') },
		})}</Text
	>

	<Select
		size="l"
		value={selectedMode}
		options={modeOptions}
		icon={'fal fa-lightbulb'}
		class="mb-s"
		on:change={handleModeChange}
		id="select-nightmode-mode"
		aria-label={$format('select_mode_property', {
			default: 'Select Mode Property',
		})}
		disabled={!isModeEnabled || disabled}
	/>

	{#if !isModeEnabled && deviceSupportsColorModeChange}
		<Alert
			type="info"
			heading={$format('alert_mode_disabled_latest_settings', {
				default:
					"When disabled, the Night Mode will use the device's currently active mode and settings instead.",
			})}
		/>
	{/if}
</Container>
